// src/components/StatusPicker.jsx
//
// Three-column status picker for PatientDetailPage, shown as a centered modal.
// Columns: General | Coordinators | Stages. Generous fixed size so options fit
// with minimal scrolling. Rendered via a portal so it's never clipped.
//
// IMPORTANT: this only changes how a status is *picked*. The value passed to
// onChange is the exact same string the old <select> produced:
//   - general statuses verbatim (e.g. "Enrolled", "Future Studies - COPD")
//   - coordinator workflow as "<Stage> (<Name>)"
//       Stage ∈ { "Pending", "Coordinator to Screen", "Schedule with Coordinator" }
// so existing patient data keeps matching exactly.

// Labels come from statusLabel ("Future Studies - IPF" shows as "- ILD"); the
// value passed to onChange is still the stored string.

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { statusLabel } from "../constants/diseaseOptions";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Check,
  ChevronDown,
  ChevronRight,
  Users,
  X,
} from "lucide-react";

const SUBSTATUS_TYPES = [
  "Pending",
  "Coordinator to Screen",
  "Schedule with Coordinator",
];

const COL_HEADER =
  "px-2 pt-1 pb-1 text-[10px] uppercase tracking-[0.18em] text-slate-400 sticky top-0 bg-slate-950/80 backdrop-blur z-10";

// Parse "<Stage> (<Name>)" into { stage, name }, else null.
const parseCoordinatorStatus = (value) => {
  if (!value) return null;
  const m = value.match(/^(.*) \((.*)\)$/);
  if (!m) return null;
  const stage = m[1].trim();
  const name = m[2].trim();
  if (!SUBSTATUS_TYPES.includes(stage)) return null;
  return { stage, name };
};

export default function StatusPicker({
  value,
  onChange,
  coordinators = [],
  baseStatuses = [],
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const parsed = parseCoordinatorStatus(value);

  const [activeCoordinator, setActiveCoordinator] = useState(
    parsed?.name || null
  );

  // Keep the coordinator embedded in the current value selectable even if they
  // were since deactivated (so an existing assignment never disappears).
  const coordinatorNames = useMemo(() => {
    const names = [...coordinators];
    if (parsed?.name && !names.includes(parsed.name)) names.push(parsed.name);
    return names;
  }, [coordinators, parsed?.name]);

  const generalStatuses = useMemo(
    () => baseStatuses.filter((s) => !s.startsWith("Future Studies")),
    [baseStatuses]
  );
  const futureStatuses = useMemo(
    () => baseStatuses.filter((s) => s.startsWith("Future Studies")),
    [baseStatuses]
  );

  const q = search.trim().toLowerCase();
  const matches = (s) =>
    !q || s.toLowerCase().includes(q) || statusLabel(s).toLowerCase().includes(q);

  const filteredGeneral = generalStatuses.filter(matches);
  const filteredFuture = futureStatuses.filter(matches);
  const filteredCoordinators = coordinatorNames.filter(
    (name) =>
      matches(name) || SUBSTATUS_TYPES.some((t) => matches(`${t} (${name})`))
  );

  // Sync state when opening; lock body scroll; Escape to close.
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setActiveCoordinator(parsed?.name || null);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choose = (val) => {
    onChange(val);
    setOpen(false);
  };

  const statusBtn = (s, label) => (
    <button
      key={s}
      type="button"
      onClick={() => choose(s)}
      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-sm transition ${
        value === s
          ? "bg-purple-500/20 text-white"
          : "text-slate-300 hover:bg-white/[0.06]"
      }`}
    >
      <span className="truncate">{label}</span>
      {value === s && (
        <Check className="w-3.5 h-3.5 ml-auto text-purple-300 shrink-0" />
      )}
    </button>
  );

  return (
    <>
      {/* Trigger (stays in flow) */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-left text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition"
      >
        <span className={value ? "text-slate-100 truncate" : "text-slate-500"}>
          {statusLabel(value) || "Select status"}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open &&
        createPortal(
          <AnimatePresence>
            <motion.div
              key="status-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
              className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setOpen(false);
              }}
            >
              <motion.div
                key="status-panel"
                initial={{ opacity: 0, scale: 0.97, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: 8 }}
                transition={{ duration: 0.16, ease: "easeOut" }}
                className="w-[min(980px,95vw)] h-[min(660px,88vh)] rounded-2xl border border-white/15 bg-slate-950/80 backdrop-blur-2xl shadow-2xl overflow-hidden flex flex-col"
              >
                {/* Header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 shrink-0">
                  <span className="text-[12px] uppercase tracking-[0.2em] font-semibold text-white">
                    Set Status
                  </span>
                  <span className="ml-auto text-[12px] text-slate-400 truncate max-w-[50%]">
                    {statusLabel(value) || "none selected"}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Search (full width) */}
                <div className="px-4 py-3 border-b border-white/10 shrink-0">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input
                      autoFocus
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search status or coordinator…"
                      className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50"
                    />
                  </div>
                </div>

                {/* Three columns: General | Coordinators | Stages */}
                <div className="flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-3 overflow-y-auto sm:overflow-hidden divide-y sm:divide-y-0 sm:divide-x divide-white/10 scrollbar-thin">
                  {/* COLUMN 1 — General + Future Studies */}
                  <div className="sm:min-h-0 sm:overflow-y-auto px-2 py-2 space-y-1 scrollbar-thin">
                    {filteredGeneral.length > 0 && (
                      <div>
                        <p className={COL_HEADER}>General</p>
                        {filteredGeneral.map((s) => statusBtn(s, s))}
                      </div>
                    )}
                    {filteredFuture.length > 0 && (
                      <div>
                        <p className={COL_HEADER}>Future Studies</p>
                        {filteredFuture.map((s) =>
                          statusBtn(s, statusLabel(s).replace("Future Studies - ", ""))
                        )}
                      </div>
                    )}
                    {filteredGeneral.length === 0 &&
                      filteredFuture.length === 0 && (
                        <p className="px-2 py-6 text-center text-[12px] text-slate-500">
                          No general matches
                        </p>
                      )}
                  </div>

                  {/* COLUMN 2 — Coordinators */}
                  <div className="sm:min-h-0 sm:overflow-y-auto px-2 py-2 space-y-1 scrollbar-thin">
                    <p className={`${COL_HEADER} flex items-center gap-1`}>
                      <Users className="w-3 h-3" /> Coordinators
                    </p>
                    {filteredCoordinators.length > 0 ? (
                      filteredCoordinators.map((name) => {
                        const isActive = activeCoordinator === name;
                        const isCurrent = parsed?.name === name;
                        return (
                          <button
                            key={name}
                            type="button"
                            onClick={() => setActiveCoordinator(name)}
                            className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-sm transition ${
                              isActive
                                ? "bg-white/[0.10] text-white"
                                : "text-slate-300 hover:bg-white/[0.06]"
                            }`}
                          >
                            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-[11px] font-bold text-white ring-1 ring-white/20 shrink-0">
                              {name.charAt(0).toUpperCase()}
                            </span>
                            <span className="truncate">{name}</span>
                            {isCurrent && (
                              <Check className="w-3.5 h-3.5 text-purple-300 shrink-0" />
                            )}
                            <ChevronRight
                              className={`w-4 h-4 ml-auto shrink-0 ${
                                isActive ? "text-white" : "text-slate-500"
                              }`}
                            />
                          </button>
                        );
                      })
                    ) : (
                      <p className="px-2 py-6 text-center text-[12px] text-slate-500">
                        No coordinators
                      </p>
                    )}
                  </div>

                  {/* COLUMN 3 — Stages for the active coordinator */}
                  <div className="sm:min-h-0 sm:overflow-y-auto p-4 scrollbar-thin">
                    {activeCoordinator ? (
                      <>
                        <div className="flex items-center gap-2 mb-4">
                          <span className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-[12px] font-bold text-white ring-1 ring-white/20 shrink-0">
                            {activeCoordinator.charAt(0).toUpperCase()}
                          </span>
                          <span className="text-base font-semibold text-white truncate">
                            {activeCoordinator}
                          </span>
                        </div>
                        <div className="space-y-2.5">
                          {SUBSTATUS_TYPES.map((stage) => {
                            const composed = `${stage} (${activeCoordinator})`;
                            const selected = value === composed;
                            return (
                              <button
                                key={stage}
                                type="button"
                                onClick={() => choose(composed)}
                                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl border text-left text-sm transition ${
                                  selected
                                    ? "bg-purple-500/20 border-purple-300/40 text-white"
                                    : "bg-white/[0.03] border-white/10 text-slate-200 hover:bg-white/[0.07] hover:border-white/20"
                                }`}
                              >
                                <span
                                  className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                                    selected
                                      ? "border-purple-300 bg-purple-400/30"
                                      : "border-slate-500"
                                  }`}
                                >
                                  {selected && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-200" />
                                  )}
                                </span>
                                <span>{stage}</span>
                                {selected && (
                                  <Check className="w-4 h-4 ml-auto text-purple-300 shrink-0" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="h-full min-h-[160px] flex flex-col items-center justify-center text-center px-4">
                        <Users className="w-7 h-7 text-slate-600 mb-2" />
                        <p className="text-[13px] text-slate-500">
                          Pick a coordinator to choose their stage.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}
